/**
 * Supabase authentication service.
 *
 * Implements {@link IAuthService} on top of the Supabase Auth SDK — the only
 * auth implementation. Session persistence, token storage, and automatic
 * refresh are handled by the SDK (PKCE flow) — we never persist raw JWTs
 * ourselves.
 *
 * LOGIN NO LONGER RESOLVES ANYTHING IN THE BROWSER. The app accepts a username
 * OR an email while GoTrue authenticates by email, so something has to map one
 * to the other BEFORE the password is checked — which is to say while the
 * caller is still anonymous. This file used to do that here, calling the
 * anon-callable `get_email_for_username` RPC (migration 013) and falling back
 * to `<username>@madivladi.com`. That RPC was a proven-live unauthenticated
 * disclosure: an anon POST to `/rest/v1/rpc/get_email_for_username` answered
 * with a real member's real email address, and its string-vs-null answer let
 * anyone walk the whole username namespace. Narrowing the grant could not fix
 * it — any grant that keeps username login working keeps the oracle working,
 * because the caller doing the resolving is by definition not yet
 * authenticated.
 *
 * So {@link SupabaseAuthService.login} now POSTs the identifier and the
 * password to the `login-with-identifier` Edge Function
 * (`supabase/functions/login-with-identifier/index.ts`). That function resolves
 * the address with the service-role key, performs the sign-in server-side, and
 * returns a session. The address reaches the browser only inside `user`, only
 * on the success path — where the caller has just proved they hold that exact
 * account's password. The client's remaining job is to ADOPT the returned
 * session with `auth.setSession`, which writes it through the switchable
 * storage adapter in `shared/clients/supabaseClient.ts` — the adapter that
 * makes „Запомни ме" mean something. Once every caller is on this path the RPC
 * has no consumer left and is dropped; this module deliberately no longer
 * calls it from anywhere.
 *
 * RECOVERY MOVED THE SAME WAY, AND HAD TO. „Забравена парола" was the last
 * identifier resolution left in the browser. With no anon-callable lookup it
 * could only GUESS — no `@` in the identifier meant `<username>@madivladi.com`,
 * full stop — and that guess is wrong for two kinds of account this project
 * actually creates: a member invited at an external address
 * (`invite-family-member` accepts any) and one whose username was auto-suffixed
 * by the signup dedupe loop (`mama` → `mama2`). GoTrue answers `200` whether or
 * not an address exists, so those members were shown „Имейлът е изпратен!" and
 * then waited forever; nothing threw and nothing was logged.
 * {@link SupabaseAuthService.forgotPassword} therefore posts the identifier to
 * the `request-password-reset` Edge Function
 * (`supabase/functions/request-password-reset/index.ts`), which resolves it
 * with the service-role key and asks GoTrue to send the mail itself. That
 * function answers `204` for EVERY outcome — resolved or not, sent or refused —
 * so it adds no oracle of its own, and this client must not invent one: it
 * resolves without telling its caller which case it was.
 *
 * DEPLOY ORDERING MATTERS. Edge Functions do not ship with the frontend build:
 * BOTH `login-with-identifier` and `request-password-reset` must be deployed
 * (`npm run supabase:functions:deploy`) BEFORE this client is released, or
 * every login — and every recovery attempt — fails at once.
 *
 * Failure stays ONE answer. The function returns a byte-identical 401 for an
 * unknown username, an unknown email and a wrong password, so the enumeration
 * oracle is not merely relocated — and this client passes that wording through
 * VERBATIM instead of re-translating it (Bulgarian text matches none of
 * {@link translateLoginError}'s English needles and would be downgraded into a
 * vaguer fallback). The ONE status that is not that 401 is `429`: the function
 * surfaces a rate-limit refusal distinctly, on both its resolved and its
 * unresolved path, because Supabase counts the password-grant limit per IP and
 * so a 429 says nothing about any account. {@link RATE_LIMIT_MESSAGE} is what
 * the member sees either way.
 *
 * Error surfaces are localized: raw GoTrue API strings are never shown to the
 * user (see {@link translateLoginError} / {@link translateSignupError}).
 *
 * @module features/auth/services/supabaseAuthService
 */

import type { Session, User as SupabaseUser } from '@supabase/supabase-js';
import { getSupabaseClient } from '@/shared/clients/supabaseClient';
import { sanitizeText } from '@/shared/lib/sanitize';
import { monitoringService } from '@/shared/services/monitoringService';
import { removeFileByValue } from '@/shared/services/storageService';
import {
  type AuthResponse,
  type OAuthProvider,
  type RegisterData,
  type User,
  UserRole,
} from '../types/auth.types';
import {
  InvalidCurrentPasswordError,
  type IAuthService,
} from './authService.types';

/**
 * The one thing the client says when login fails for a reason the Edge
 * Function did not word itself: a network failure, a missing deployment, a
 * gateway error page, a 2xx with no tokens in it. Identifier-INDEPENDENT, so
 * it carries no enumeration signal — it happens for every identifier or for
 * none. Matches {@link translateLoginError}'s own fallback wording so the two
 * surfaces read the same.
 */
const GENERIC_LOGIN_FAILURE = 'Влизането е неуспешно. Опитайте отново.';

/**
 * The equivalent for „Забравена парола": a transport failure, a
 * `request-password-reset` that was never deployed, a gateway error page.
 *
 * It is deliberately about the REQUEST rather than the account, because the
 * function itself never says anything about the account — it answers `204` for
 * every identifier, real or not. Only a failure the function did not produce
 * can reach this string.
 */
const GENERIC_RECOVERY_FAILURE =
  'Заявката за нова парола е неуспешна. Опитайте отново.';

/**
 * The one wording for a rate-limit refusal, wherever it surfaces.
 *
 * ONE OWNER, THREE READERS. {@link translateLoginError} returns it for GoTrue's
 * own rate-limit phrasings; {@link SupabaseAuthService.login} falls back to it
 * for a `429` whose body could not be read; and
 * `supabase/functions/login-with-identifier/index.ts` puts this exact sentence
 * in its `429` body, which the login path then surfaces verbatim. The Edge
 * Function has to duplicate the literal because Deno cannot import from `src/`
 * — if this is reworded, reword it there too, or a member sees two different
 * sentences for the same condition depending on which layer refused.
 */
const RATE_LIMIT_MESSAGE = 'Твърде много опити. Опитайте отново след малко.';

/** Redirect target the OAuth provider returns to after authentication. */
function oauthRedirectTo(): string {
  return `${window.location.origin}/auth/callback`;
}

// WHERE THE RECOVERY HELPERS WENT. Two functions used to live here:
// `recoveryEmailFor`, which guessed `<username>@madivladi.com` because no
// anon-callable username lookup was left to ask, and `recoveryRedirectTo`,
// which built `${window.location.origin}/reset-password` for the mail link.
// Both moved into `supabase/functions/request-password-reset/index.ts` and
// changed character on the way: the address is now RESOLVED with the
// service-role key instead of guessed, and the redirect target is DERIVED from
// the request's allow-listed `Origin` server-side instead of being taken from
// the caller — which, in a password-recovery mail, is the difference between a
// link home and an open redirect that hands the recovery token to whoever asked
// for it. Do not reintroduce either here: an address the browser can compute is
// an address the browser got wrong, and a redirect the browser can choose is a
// redirect an attacker can choose.

/** Map a Supabase auth user into the app {@link User} shape. */
function mapSupabaseUser(supabaseUser: SupabaseUser): User {
  const metadata = (supabaseUser.user_metadata ?? {}) as Record<
    string,
    unknown
  >;
  const email = supabaseUser.email ?? '';
  const metaUsername = metadata['username'];
  const metaName = metadata['name'];
  const username =
    (typeof metaUsername === 'string' && metaUsername) ||
    email.split('@')[0] ||
    'user';
  const name = (typeof metaName === 'string' && metaName) || username;
  // Roles are NEVER inferred (from username, from metadata, or otherwise) —
  // they originate solely from invitation/membership data (AC-30). This line
  // used to read `user_metadata.role` and fall back to CHILD, which made the
  // invariant one line above it false: `user_metadata` is writable by the
  // account holder (`supabase.auth.updateUser({ data: { role: … } })`), so a
  // CHILD with devtools could hand this mapper a SUPER_ADMIN and every client
  // gate that keys off `User.role` — PermissionGate, MemberList's
  // `canManageMember`, the role-conditional routes — would believe it. RLS
  // still refused the actual writes, so the damage was a UI that offered
  // actions the server would reject, but a client gate nobody can trust is
  // not a gate.
  //
  // Nothing legitimate produced that key either: `register` deliberately never
  // sends a role (see the comment there) and the founder role is applied
  // out-of-band from the signup result. So the read had no producer and one
  // forger. The placeholder below is what every caller gets until
  // `familyStore` re-syncs the real per-family role from the `family_members`
  // row through `useAuthStore.getState().setUserRole(...)` — the only source
  // the server actually authorises.
  const role = UserRole.CHILD;
  const metaAvatar = metadata['avatar_url'];

  return {
    id: supabaseUser.id,
    username,
    email,
    name,
    role,
    permissions: [],
    createdAt: supabaseUser.created_at,
    ...(supabaseUser.last_sign_in_at
      ? { lastLoginAt: supabaseUser.last_sign_in_at }
      : {}),
    ...(typeof metaAvatar === 'string' && metaAvatar
      ? { avatarUrl: metaAvatar }
      : {}),
  };
}

/** Build an {@link AuthResponse} from a Supabase user + session. */
function toAuthResponse(
  supabaseUser: SupabaseUser,
  session: Session | null
): AuthResponse {
  return {
    user: mapSupabaseUser(supabaseUser),
    token: session?.access_token ?? '',
    ...(session?.refresh_token ? { refreshToken: session.refresh_token } : {}),
    expiresIn: session?.expires_in ?? 0,
  };
}

/**
 * The `context` a Supabase `FunctionsHttpError` carries: the raw `Response`,
 * still unread — the SDK rejects on a non-2xx before touching the body.
 *
 * `status` is read on exactly one path ({@link SupabaseAuthService.login}'s
 * `429` fallback) and is optional because this is a structural view of an SDK
 * object, not a contract the SDK publishes.
 */
interface FunctionsErrorContext {
  readonly status?: number;
  readonly json?: () => Promise<unknown>;
}

/**
 * Pull the localized message out of an Edge Function failure body
 * (`{ error: string }`), or null when the body carries nothing usable.
 */
function edgeFunctionErrorMessage(payload: unknown): string | null {
  if (
    typeof payload !== 'object' ||
    payload === null ||
    !('error' in payload)
  ) {
    return null;
  }
  const { error } = payload;
  return typeof error === 'string' && error.length > 0 ? error : null;
}

/**
 * The `200` body of `login-with-identifier`: the session fields the SDK needs
 * to adopt the session, plus the account's own user row.
 *
 * Only the two tokens are named here, because they are the only fields this
 * client reads — `expires_in`, `expires_at`, `token_type` and `user` all come
 * back authoritative from `auth.setSession`, which re-derives them from the
 * access token rather than trusting a response body. Declared as `string` and
 * still checked at runtime: this is network JSON, not a compiler guarantee.
 */
interface LoginSessionPayload {
  readonly access_token: string;
  readonly refresh_token: string;
}

/**
 * Real authentication service backed by Supabase Auth.
 */
export class SupabaseAuthService implements IAuthService {
  private get auth() {
    return getSupabaseClient().auth;
  }

  async login(username: string, password: string): Promise<AuthResponse> {
    // No resolution happens here any more — the whole sign-in is delegated to
    // the `login-with-identifier` Edge Function so the username→email mapping
    // never has to be reachable by an unauthenticated browser. See the module
    // banner for why narrowing the old RPC's grant could not have worked.
    //
    // `functions.invoke` sends `Authorization: Bearer <anon key>` when there is
    // no session, which is exactly what the function's `verify_jwt = true`
    // gateway check consumes. There is nothing stronger to send: producing a
    // session is the point of the call.
    //
    // The identifier goes over untrimmed and unfolded — the function trims and
    // lowercases it, and one owner of that rule is better than two. The
    // password is passed through byte-for-byte: leading or trailing whitespace
    // can be part of a real password and must never be silently stripped.
    const response = await getSupabaseClient().functions.invoke<
      LoginSessionPayload | { error: string }
    >('login-with-identifier', {
      body: { identifier: username, password },
    });

    if (response.error) {
      // The SDK rejects on ANY non-2xx before it parses the body, so
      // `error.message` is the English literal 'Edge Function returned a
      // non-2xx status code' and the function's own Bulgarian refusal survives
      // only on the untouched `Response` behind `context` — unwrapped here the
      // same way `parentChangePassword` and `inviteMember` do it.
      //
      // That refusal is surfaced VERBATIM, never through
      // `translateLoginError`: it is already the single generic message the
      // member should see, and running Bulgarian text through a map of English
      // needles would match nothing and downgrade a precise message into a
      // vaguer one.
      const { context } = response.error as {
        context?: FunctionsErrorContext;
      };
      let functionMessage: string | null = null;
      if (typeof context?.json === 'function') {
        try {
          functionMessage = edgeFunctionErrorMessage(await context.json());
        } catch {
          // Body already consumed, empty, or not JSON — a gateway error page,
          // a 404 from a function that was never deployed, a CORS-blocked or
          // offline request. The fallback below is all we can honestly say,
          // and it says nothing about any account.
        }
      }

      // A `429` is the one status this client reads off the raw `Response`, and
      // it is a FALLBACK rather than the primary path: the Edge Function
      // already answers 429 with {@link RATE_LIMIT_MESSAGE} in the body, so the
      // verbatim pass-through above normally carries it. The status is checked
      // because a 429 can arrive with no readable body at all — the Supabase
      // gateway rate-limits ahead of the function and answers with its own
      // shape, and a CORS-blocked or already-consumed body leaves
      // `functionMessage` null. Without this branch a locked-out member would
      // read „Влизането е неуспешно. Опитайте отново." and do exactly the wrong
      // thing, which is retry immediately.
      const fallback =
        context?.status === 429 ? RATE_LIMIT_MESSAGE : GENERIC_LOGIN_FAILURE;
      throw new Error(functionMessage ?? fallback);
    }

    const payload = response.data;
    if (
      payload === null ||
      'error' in payload ||
      typeof payload.access_token !== 'string' ||
      typeof payload.refresh_token !== 'string'
    ) {
      // A 2xx carrying no tokens is a broken deployment, not a wrong password.
      // It still gets the generic wording: the difference is not actionable by
      // the member, and inventing a distinct one for it would be a new signal
      // on a path an attacker can reach.
      throw new Error(GENERIC_LOGIN_FAILURE);
    }

    // Adopt the session through the SDK instead of holding the raw tokens
    // ourselves. `setSession` writes it through the switchable storage adapter
    // in shared/clients/supabaseClient.ts, which is what makes „Запомни ме"
    // real — a session written any other way is not remembered. The ordering
    // works because the auth store calls `setSessionPersistence` for this
    // attempt BEFORE it calls this method (features/auth/stores/authStore.ts),
    // so the adapter is already in the right mode and this single write lands
    // in the right Web Storage area with no migration needed. `setSession`
    // also emits `SIGNED_IN`, which warms the token cache the synchronous auth
    // bridge reads, and starts the SDK's refresh timer.
    //
    // Known cost, so nobody "optimises" it away by mistake: auth-js does not
    // trust the tokens blindly — it decodes the access token and, when it is
    // still valid, spends one extra `GET /auth/v1/user` to hydrate the user
    // before saving. So a login is now two round trips, and the `user`,
    // `expires_in`, `expires_at` and `token_type` fields the function returns
    // are informational: everything below comes from what `setSession` gives
    // back, which the server just re-derived. The tail risk is a network blip
    // between the two calls — GoTrue has recorded a successful sign-in and the
    // member is told login failed. Retrying is safe and is the right answer.
    const { data, error } = await this.auth.setSession({
      access_token: payload.access_token,
      refresh_token: payload.refresh_token,
    });
    if (error || !data.user || !data.session) {
      // The tokens were minted seconds ago by GoTrue itself, so this is a
      // client-side or clock-skew fault rather than an authentication result.
      // Generic wording again, for the same reason as above.
      throw new Error(GENERIC_LOGIN_FAILURE);
    }
    return toAuthResponse(data.user, data.session);
  }

  async register(data: RegisterData): Promise<AuthResponse> {
    // Family-first metadata: the DB trigger provisions family + profile +
    // membership from exactly one of these paths. The role is NEVER sent —
    // founders become SUPER_ADMIN, invited users get the invitation's role.
    //
    // EVERY FREE-TEXT FIELD GOES THROUGH `sanitizeText`, and the service is the
    // place that does it rather than the form. `RegisterForm` sanitizes too,
    // but a form guard only covers the one caller that happens to be a form:
    // `useAuthStore.register` is callable directly, and the trigger behind this
    // `signUp` copies `username`, `name` and `family_name` straight out of the
    // raw metadata into `profiles` and `families` — so unsanitized text here
    // becomes unsanitized text in a column that every member list renders.
    // Same choke-point rule the todo, calendar and album services already
    // follow. `invite_code` is deliberately NOT run through it: it is a fixed
    // alphanumeric token matched against a row, not free text, and the
    // trim/upper-case normalisation it needs is not what `sanitizeText` does.
    const metadata: Record<string, string> = {
      username: sanitizeText(data.username),
      name: sanitizeText(data.name),
    };
    if (data.inviteCode) {
      metadata['invite_code'] = data.inviteCode.trim().toUpperCase();
    } else if (data.familyName) {
      metadata['family_name'] = sanitizeText(data.familyName);
    }

    const { data: result, error } = await this.auth.signUp({
      email: data.email,
      password: data.password,
      options: {
        data: metadata,
      },
    });
    if (error) {
      throw new Error(translateSignupError(error.message));
    }
    if (!result.user) {
      throw new Error('Неуспешна регистрация: липсва потребител в отговора.');
    }
    if (!result.session) {
      // Email confirmation is still required (misprovisioned project — the
      // repo config disables confirmations). Returning a token-less "success"
      // would create a zombie authenticated state where every data call
      // fails; surface it explicitly instead.
      throw new Error(
        'Регистрацията изисква потвърждение на имейл. Проверете пощата си, преди да влезете.'
      );
    }
    const response = toAuthResponse(result.user, result.session);
    if (!data.inviteCode) {
      // Founder path: the trigger provisioned this account as SUPER_ADMIN of
      // the new family. Auth metadata carries no role, so reflect the known
      // founder role immediately; family hydration re-syncs per-family roles.
      return {
        ...response,
        user: { ...response.user, role: UserRole.SUPER_ADMIN },
      };
    }
    return response;
  }

  async logout(): Promise<void> {
    const { error } = await this.auth.signOut();
    if (error) {
      throw new Error(error.message);
    }
  }

  async refreshToken(token: string): Promise<AuthResponse> {
    const { data, error } = await this.auth.refreshSession({
      refresh_token: token,
    });
    if (error) {
      throw new Error(error.message);
    }
    if (!data.user) {
      throw new Error('Неуспешно обновяване на сесията.');
    }
    return toAuthResponse(data.user, data.session);
  }

  async getCurrentUser(): Promise<User> {
    const { data, error } = await this.auth.getUser();
    if (error) {
      throw new Error(error.message);
    }
    if (!data.user) {
      throw new Error('Няма активен потребител.');
    }
    return mapSupabaseUser(data.user);
  }

  /**
   * The avatar URL currently stored in auth metadata, or null when there is
   * none / it cannot be read.
   *
   * Best-effort by construction: it exists only so {@link updateAvatar} knows
   * which storage object it is about to orphan, and an avatar change must not
   * fail because that lookup did. A `null` here costs one leaked object, which
   * is a bill; throwing would cost the user their new profile picture.
   */
  private async previousAvatarUrl(): Promise<string | null> {
    try {
      const { data, error } = await this.auth.getUser();
      if (error || !data.user) {
        return null;
      }
      const metadata = (data.user.user_metadata ?? {}) as Record<
        string,
        unknown
      >;
      const stored = metadata['avatar_url'];
      return typeof stored === 'string' && stored ? stored : null;
    } catch {
      return null;
    }
  }

  async updateAvatar(avatarUrl: string): Promise<User> {
    // THE OLD OBJECT IS REMOVED HERE, not at the call site, and that placement
    // is the whole point. `uploadFile` mints a fresh UUID for every key, so no
    // upload ever overwrites the previous avatar — every change used to leave
    // the old object in the bucket forever, and nothing reclaims it:
    // `cleanup-orphan-storage` scans the `photos` bucket only, so `avatars`
    // has to clean up after itself. The removal used to live in
    // `AvatarUploader.processFile`, which worked only for as long as that one
    // component stayed the only caller. This method is the single choke point
    // every write of `user_metadata.avatar_url` / `profiles.avatar_url` passes
    // through, so a second caller — a settings screen, an onboarding step, an
    // admin action — now inherits the policy instead of silently reintroducing
    // the leak.
    //
    // Read the outgoing value FIRST: `updateUser` answers with the new user,
    // so after the write there is nothing left to compare against.
    const previous = await this.previousAvatarUrl();

    const { data, error } = await this.auth.updateUser({
      data: { avatar_url: avatarUrl },
    });
    if (error) {
      throw new Error(error.message);
    }
    if (!data.user) {
      throw new Error('Неуспешно обновяване на профилната снимка.');
    }

    // Mirror into public.profiles so family member lists (which join
    // profiles, not auth metadata) render the avatar too. Non-fatal: the
    // auth metadata remains the primary source for the current user.
    const { error: profileError } = await getSupabaseClient()
      .from('profiles')
      .update({ avatar_url: avatarUrl })
      .eq('id', data.user.id);
    if (profileError) {
      monitoringService.captureError(
        new Error(`Avatar profile sync failed: ${profileError.message}`),
        {
          tags: { feature: 'auth', action: 'updateAvatar' },
          level: 'warning',
        }
      );
    }

    // ORDER AND PRECONDITIONS. Removal comes last, after BOTH writes, so a
    // failure anywhere above leaves the member with the picture they already
    // had rather than with none. `profileError` is a hard stop for the removal
    // even though it is not one for the update: when the mirror write fails,
    // `profiles.avatar_url` still points at the old object, so deleting it
    // would blank the avatar out of every member list that joins that table.
    // A leaked object is cheaper than a broken one.
    //
    // The call itself is best-effort and never surfaces: `removeFileByValue`
    // swallows its own failures and declines to act on a value it cannot
    // resolve unambiguously (an avatar hosted off-project, an OAuth provider's
    // picture URL, a stored signed URL).
    if (!profileError && previous !== null && previous !== avatarUrl) {
      await removeFileByValue('avatars', previous);
    }

    return mapSupabaseUser(data.user);
  }

  async verifyToken(token: string): Promise<boolean> {
    const { data, error } = await this.auth.getUser(token);
    return !error && Boolean(data.user);
  }

  async changePassword(
    currentPassword: string,
    newPassword: string
  ): Promise<void> {
    // Supabase's updateUser does not verify the old password, so re-auth
    // first: a failed signInWithPassword means the current password is wrong.
    const { data: userData, error: userError } = await this.auth.getUser();
    if (userError || !userData.user?.email) {
      throw new Error('Няма активен потребител.');
    }
    const { error: reauthError } = await this.auth.signInWithPassword({
      email: userData.user.email,
      password: currentPassword,
    });
    if (reauthError) {
      throw new InvalidCurrentPasswordError();
    }
    const { error } = await this.auth.updateUser({ password: newPassword });
    if (error) {
      throw new Error(error.message);
    }
  }

  async forgotPassword(username: string): Promise<void> {
    // Nothing is resolved here any more. The identifier — a username OR an
    // email — goes to the `request-password-reset` Edge Function, which
    // resolves it with the service-role key, derives the recovery link's
    // redirect target from the allow-listed request `Origin` server-side, and
    // asks GoTrue to send the mail. See the module banner for the guess this
    // replaced and who it was silently failing.
    //
    // The identifier goes over untrimmed and unfolded, exactly as in `login`:
    // the function trims and lowercases it, and one owner of that rule is
    // better than two.
    //
    // The function answers `204` for EVERY outcome — resolved or not, sent or
    // refused, rate-limited or not. That is deliberate and this method must not
    // undo it: any distinction the client added back would be the enumeration
    // oracle in a new place, and the recovery form's confirmation text is
    // written to be true whether or not the identifier named anybody. So a 2xx
    // resolves and says nothing. Only a TRANSPORT failure rejects.
    const response = await getSupabaseClient().functions.invoke<unknown>(
      'request-password-reset',
      { body: { identifier: username } }
    );

    if (response.error) {
      // Same unwrapping as `login` and `parentChangePassword`: the SDK rejects
      // on ANY non-2xx before it parses the body, so `error.message` is the
      // English literal 'Edge Function returned a non-2xx status code' and the
      // function's own Bulgarian wording survives only on the untouched
      // `Response` behind `context`.
      //
      // Only the 400 (a malformed body) and the 500 (a misconfigured
      // deployment) can reach here from the function itself; everything else
      // that lands here never reached it at all — a 404 from a release that
      // forgot `supabase functions deploy`, a gateway error page, an offline
      // device. No `429` branch is needed: recovery rate limits are keyed per
      // address, so the function refuses to distinguish them and answers 204.
      const { context } = response.error as {
        context?: FunctionsErrorContext;
      };
      let functionMessage: string | null = null;
      if (typeof context?.json === 'function') {
        try {
          functionMessage = edgeFunctionErrorMessage(await context.json());
        } catch {
          // Body already consumed, empty, or not JSON. The generic message
          // below is all we can honestly say — and it must be Bulgarian, or
          // the SDK's English literal reaches an all-Bulgarian UI.
        }
      }
      throw new Error(functionMessage ?? GENERIC_RECOVERY_FAILURE);
    }
  }

  async resetPassword(newPassword: string): Promise<void> {
    // No token is exchanged here: the PKCE recovery flow has the SDK consume
    // the emailed link and establish the recovery session, so the caller
    // already holds it; updateUser applies the change to that session.
    const { error } = await this.auth.updateUser({ password: newPassword });
    if (error) {
      throw new Error(
        translateLoginError(
          error.message,
          'Паролата не беше променена. Опитайте отново.'
        )
      );
    }
  }

  async parentChangePassword(
    userId: string,
    newPassword: string
  ): Promise<void> {
    // Resetting another user's password requires the service-role key, which is
    // never exposed to the browser. The privileged update is delegated to the
    // `admin-change-password` Edge Function, which re-validates the caller
    // server-side before applying the change: at least PARENT RANK (never role
    // equality, which would lock the SUPER_ADMIN founder out) in a family the
    // target also belongs to, a rank in THAT family strictly higher than the
    // target's (peers are refused), and never the caller themselves —
    // self-service is `changePassword`, which re-authenticates.
    //
    // Every refusal the function returns is deliberate, user-facing Bulgarian —
    // but the SDK rejects on ANY non-2xx before it parses the body, leaving
    // `data` null and `error.message` at the English literal 'Edge Function
    // returned a non-2xx status code'. The function's own wording is therefore
    // only reachable through the error's `context` (the untouched `Response`),
    // which is unwrapped below the same way `inviteMember` does it.
    const response = await getSupabaseClient().functions.invoke<{
      success?: boolean;
    }>('admin-change-password', {
      body: { userId, newPassword },
    });

    if (response.error) {
      const { context } = response.error as {
        context?: FunctionsErrorContext;
      };
      let functionMessage: string | null = null;
      if (typeof context?.json === 'function') {
        try {
          functionMessage = edgeFunctionErrorMessage(await context.json());
        } catch {
          // Body already consumed, empty, or not JSON (e.g. a gateway error
          // page) — the generic message below is all we can honestly say.
        }
      }
      throw new Error(functionMessage ?? 'Смяната на паролата е неуспешна.');
    }
  }

  async signInWithOAuth(provider: OAuthProvider): Promise<void> {
    const { error } = await this.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: oauthRedirectTo(),
      },
    });
    if (error) {
      throw new Error(error.message);
    }
    // On success the browser is redirected to the provider; control does not
    // return here. The session is completed later on `/auth/callback`.
  }

  async completeOAuthSession(): Promise<AuthResponse> {
    // `detectSessionInUrl: true` means the SDK has already exchanged the PKCE
    // code for a session by the time this runs; we just read it back.
    const { data, error } = await this.auth.getSession();
    if (error) {
      throw new Error(error.message);
    }
    if (!data.session?.user) {
      throw new Error('Неуспешно влизане: липсва сесия след OAuth.');
    }
    return toAuthResponse(data.session.user, data.session);
  }

  async completeInvitedAccount(data: {
    readonly name: string;
    readonly username: string;
    readonly password: string;
  }): Promise<AuthResponse> {
    // The invite link established the session (detectSessionInUrl); the
    // membership + profile already exist from the signup trigger (path b) —
    // this finalises the account with the user's chosen identity + password.
    //
    // Identity fields are sanitized here, at the service, for the same reason
    // as in `register`. The PASSWORD is not, and must never be: `sanitizeText`
    // normalises whitespace and strips `<…>`, so running a password through it
    // would silently change the secret being stored and lock its owner out of
    // an account they just created.
    const username = sanitizeText(data.username);
    const name = sanitizeText(data.name);
    const { data: result, error } = await this.auth.updateUser({
      password: data.password,
      data: { username, name },
    });
    if (error) {
      throw new Error(error.message);
    }
    if (!result.user) {
      throw new Error('Неуспешно завършване на акаунта.');
    }

    // Mirror into public.profiles so member lists (which join profiles)
    // render the chosen identity. Non-fatal: auth metadata is primary.
    const { error: profileError } = await getSupabaseClient()
      .from('profiles')
      .update({ username, name })
      .eq('id', result.user.id);
    if (profileError) {
      monitoringService.captureError(
        new Error(`Invited profile sync failed: ${profileError.message}`),
        {
          tags: { feature: 'auth', action: 'completeInvitedAccount' },
          level: 'warning',
        }
      );
    }

    const { data: sessionData } = await this.auth.getSession();
    return toAuthResponse(result.user, sessionData.session);
  }
}

/**
 * Map GoTrue auth failures to localized user-facing text. Raw GoTrue strings
 * are never surfaced. Unknown identifier and wrong password intentionally share
 * ONE generic message (no account-enumeration signal).
 *
 * WHO STILL CALLS THIS, and it is fewer surfaces than it once was. Sign-in no
 * longer does: `login` never sees a GoTrue message, because the
 * `login-with-identifier` Edge Function refuses in Bulgarian itself and that
 * wording is surfaced verbatim. Requesting a recovery mail no longer does
 * either, for the same reason. What is left is the surface that still talks to
 * GoTrue directly from the browser — {@link SupabaseAuthService.resetPassword},
 * which applies the new password to a live recovery session. The sign-in
 * branches below are kept rather than pruned because the two clients share this
 * map's vocabulary: {@link RATE_LIMIT_MESSAGE} is the same sentence the Edge
 * Function returns, and keeping one owner for it is the point.
 *
 * @param fallback Text for an unrecognised message; defaults to the sign-in
 * wording, so callers on another surface pass their own.
 */
function translateLoginError(
  message: string,
  fallback = 'Влизането е неуспешно. Опитайте отново.'
): string {
  const lower = message.toLowerCase();
  if (lower.includes('invalid login credentials')) {
    return 'Грешно потребителско име/имейл или парола.';
  }
  if (lower.includes('email not confirmed')) {
    return 'Имейлът не е потвърден. Проверете пощата си, преди да влезете.';
  }
  if (
    lower.includes('rate limit') ||
    lower.includes('too many requests') ||
    lower.includes('security purposes')
  ) {
    return RATE_LIMIT_MESSAGE;
  }
  if (
    lower.includes('new password should be different') ||
    lower.includes('same_password')
  ) {
    return 'Новата парола трябва да е различна от старата.';
  }
  if (
    lower.includes('password should be at least') ||
    lower.includes('weak_password') ||
    lower.includes('password is too weak')
  ) {
    return 'Паролата е твърде слаба. Изберете по-дълга и по-сложна парола.';
  }
  if (lower.includes('auth session missing')) {
    return 'Сесията е изтекла. Влезте отново или поискайте нова връзка.';
  }
  return fallback;
}

/**
 * Map signup-trigger exception messages to localized user-facing text. Raw
 * GoTrue strings are never surfaced (generic Bulgarian fallback).
 */
function translateSignupError(message: string): string {
  if (message.includes('signup requires a family')) {
    return 'Регистрацията изисква създаване на семейство или валидна покана.';
  }
  if (message.includes('invitation not found')) {
    return 'Невалиден код за присъединяване.';
  }
  if (message.includes('invitation revoked')) {
    return 'Тази покана е оттеглена.';
  }
  if (message.includes('invitation expired')) {
    return 'Тази покана е изтекла.';
  }
  if (message.includes('invitation exhausted')) {
    return 'Тази покана вече е използвана максималния брой пъти.';
  }
  if (message.includes('bound to a different email')) {
    return 'Тази покана е за друг имейл адрес.';
  }
  if (message.includes('invalid family name')) {
    return 'Невалидно име на семейство.';
  }
  if (
    message.includes('User already registered') ||
    message.includes('already been registered')
  ) {
    return 'Вече съществува акаунт с този имейл.';
  }
  if (message.includes('Database error saving new user')) {
    // Hosted GoTrue wraps handle_new_user trigger raises (e.g. an invalid
    // invitation) in this opaque text — the specific reason is unavailable.
    return 'Регистрацията е неуспешна. Проверете данните или кода за покана и опитайте отново.';
  }
  return 'Възникна грешка при регистрацията. Опитайте отново.';
}
